import { useState } from 'react';
import { CharlieButton } from '@ver.id/libs.ui.charlie';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { type ClientInfo, type RequestType } from '../../types';
import { useTranslation } from '../../i18n/i18n';
import { useAppPresentation } from '../../hooks/useAppPresentation';
import DesktopLayout from '../../layouts/DesktopLayout';
import FaqModal, { type FaqTopic } from '../../components/FaqModal';
import NeedHelpSection from '../../components/NeedHelpSection';
import DesktopFlowFooter from '../../components/DesktopFlowFooter';
import LoadingState from '../../components/LoadingState';
import DataTableLayout from '../../components/DataTableLayout';
import DataTable from '../../components/DataTable';

type Props = {
  presentationCompact: string;
  client?: ClientInfo;
  requestType?: RequestType;
  requestedDataGroups?: RequestedDataGroup[];
  onAccept: () => void;
  onCancel: () => void;
};

export default function DesktopAppPresentationPage({
  presentationCompact,
  client,
  requestType,
  requestedDataGroups,
  onAccept,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [faqTopic, setFaqTopic] = useState<FaqTopic | null>(null);
  const { result, selectedIndex, setSelectedIndex, isReady } =
    useAppPresentation(presentationCompact, requestedDataGroups);
  const isIssuance = requestType === 'issuance';
  const clientName = client?.name || t('dataReview.unknownClient');

  if (!isReady) {
    return (
      <DesktopLayout>
        <LoadingState title={t('dataReview.loadingNames')} />
      </DesktopLayout>
    );
  }

  if (!result.ok) {
    return (
      <DesktopLayout>
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="flex flex-col items-center gap-4 text-center max-w-lg">
            <p className="text-sm text-destructive">
              {t('dataReview.errorLoadingNames')}
            </p>
            <CharlieButton variant="outline" onClick={onCancel}>
              {t('dataReview.cancel')}
            </CharlieButton>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const { valueMap } = result;

  return (
    <DesktopLayout>
      <div className="flex flex-1 flex-col items-center justify-center gap-12 w-full">
        <div className="flex flex-col items-center gap-3 text-center max-w-lg">
          <h2 className="text-2xl font-semibold text-foreground">
            {t('dataReview.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isIssuance
              ? t('dataReview.subtitleIssuance', { clientName })
              : t('dataReview.subtitleDisclosure', { clientName })}
          </p>
        </div>

        <div className="w-full min-w-lg max-w-2xl">
          <DataTableLayout
            groups={requestedDataGroups!}
            selectedIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
            issuedByKey="dataReview.issuedBy"
            variant="desktop"
          >
            {(activeGroup) => {
              const credentialValues = valueMap.get(activeGroup.credentialUuid);
              return (
                <DataTable
                  group={activeGroup}
                  columns={[
                    {
                      header: t('dataReview.attribute'),
                      render: (f) => f.name,
                    },
                    {
                      header: t('dataReview.value'),
                      render: (f) =>
                        credentialValues?.get(f.attributeUuid) ?? '-',
                    },
                  ]}
                />
              );
            }}
          </DataTableLayout>
        </div>

        <DesktopFlowFooter
          primaryLabel={
            isIssuance
              ? t('dataReview.acceptReceive')
              : t('dataReview.acceptShare')
          }
          onPrimary={onAccept}
          secondaryLabel={t('dataReview.cancel')}
          onSecondary={onCancel}
        />

        <NeedHelpSection
          topics={['howDataProtected']}
          onSelectTopic={setFaqTopic}
        />
        <FaqModal
          topic={faqTopic}
          onChangeTopic={setFaqTopic}
          onClose={() => setFaqTopic(null)}
        />
      </div>
    </DesktopLayout>
  );
}
